import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import { startAuthentication } from '@simplewebauthn/browser';
import { Button, Input, Label, LogoMark, toast } from '@caratuva/ui';
import { z } from 'zod';
import {
  ApiError,
  googleSignIn,
  passkeyAuthenticateOptions,
  passkeyAuthenticateVerify,
  requestMagicLink,
} from '../lib/api';
import { setToken, ensureConfiguredMode } from '../lib/auth';
import { hasGoogleClientId, requestGoogleCredential } from '../lib/google-identity';

// Consumer sign-in for GetPaid: passwordless magic-link, Google, and passkeys.
// The magic-link endpoint always responds {status:'sent'}, so the "check your
// email" state renders regardless of whether the email is registered.
const LoginSearch = z.object({
  ref: z.string().optional(),
  reason: z.string().optional(),
});

export const Route = createFileRoute('/login')({
  validateSearch: LoginSearch,
  component: LoginPage,
});

// Referral codes ride in on ?ref= and are stashed for the whole funnel so the
// code survives the magic-link round-trip. Matches the dashboard's contract.
const REFERRAL_STORAGE_KEY = 'caratuva.referralCode';
const REFERRAL_CODE_RE = /^[A-Za-z0-9_-]{4,32}$/;

function readReferralCode(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const stored = window.localStorage.getItem(REFERRAL_STORAGE_KEY);
  return stored && REFERRAL_CODE_RE.test(stored) ? stored : undefined;
}

type Pending = null | 'email' | 'google' | 'passkey';

function LoginPage(): JSX.Element {
  const intl = useIntl();
  const { ref, reason } = Route.useSearch();
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState<Pending>(null);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Capture ?ref= into localStorage (once) so it survives the funnel. Only a
  // well-formed code is persisted; anything else is ignored, leaving any
  // previously stored code untouched.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (ref && REFERRAL_CODE_RE.test(ref)) {
      window.localStorage.setItem(REFERRAL_STORAGE_KEY, ref);
    }
  }, [ref]);

  // Show the invited banner whenever a valid code is in play, whether it just
  // arrived on the URL or was already stashed from an earlier visit.
  const hasReferral = Boolean((ref && REFERRAL_CODE_RE.test(ref)) || readReferralCode());

  function resolveError(err: unknown, fallbackId: string, fallbackMsg: string): string {
    if (err instanceof ApiError) return err.message;
    if (err instanceof Error && err.message) return err.message;
    return intl.formatMessage({ id: fallbackId, defaultMessage: fallbackMsg });
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (pending) return;
    setPending('email');
    setError(null);
    try {
      await requestMagicLink(email.trim(), readReferralCode());
      setSent(true);
    } catch (err) {
      setError(resolveError(err, 'auth.login.error.generic', 'Sign-in failed. Please try again.'));
    } finally {
      setPending(null);
    }
  }

  async function onGoogle(): Promise<void> {
    if (pending) return;
    setPending('google');
    setError(null);
    try {
      const credential = await requestGoogleCredential();
      const { token } = await googleSignIn(credential, readReferralCode());
      setToken(token);
      await ensureConfiguredMode();
      window.location.assign('/');
    } catch (err) {
      // A cancelled / dismissed Google prompt is a normal outcome — surface it
      // as a gentle toast instead of the error banner.
      if (err instanceof Error && err.message === 'Google sign-in was cancelled.') {
        toast(
          intl.formatMessage({
            id: 'auth.login.error.googleCancelled',
            defaultMessage: 'Google sign-in was cancelled.',
          }),
        );
      } else {
        setError(
          resolveError(
            err,
            'auth.login.error.google',
            'Google sign-in didn’t work. Please try again.',
          ),
        );
      }
      setPending(null);
    }
  }

  async function onPasskey(): Promise<void> {
    if (pending) return;
    setPending('passkey');
    setError(null);
    try {
      const opts = await passkeyAuthenticateOptions();
      const resp = await startAuthentication({
        optionsJSON: opts as Parameters<typeof startAuthentication>[0]['optionsJSON'],
      });
      const { token } = await passkeyAuthenticateVerify(resp);
      setToken(token);
      await ensureConfiguredMode();
      window.location.assign('/');
    } catch (err) {
      // The user cancelling the WebAuthn prompt (or having no credential)
      // surfaces as a DOMException named 'NotAllowed' / 'AbortError'. Treat it
      // as a quiet cancellation, not a hard error.
      const name = err instanceof Error ? err.name : '';
      if (name === 'NotAllowedError' || name === 'AbortError') {
        toast(
          intl.formatMessage({
            id: 'auth.login.error.passkeyCancelled',
            defaultMessage: 'Passkey sign-in was cancelled.',
          }),
        );
      } else {
        setError(
          resolveError(
            err,
            'auth.login.error.passkey',
            'Passkey sign-in didn’t work. Please try again.',
          ),
        );
      }
      setPending(null);
    }
  }

  const showGoogle = hasGoogleClientId();

  return (
    <main className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm rounded-lg border border-navy-200 bg-white p-8 shadow-sm">
        <header className="mb-6 flex flex-col items-center gap-3 text-navy-900">
          <LogoMark size={44} />
          <h1
            className="text-xl font-medium tracking-tight"
            style={{ fontFamily: 'var(--font-serif)' }}
          >
            {intl.formatMessage({ id: 'auth.login.title', defaultMessage: 'Sign in to GetPaid' })}
          </h1>
          <p className="text-center text-sm text-navy-600">
            {intl.formatMessage({
              id: 'auth.login.subtitle',
              defaultMessage: 'Enter your email and we’ll send you a magic sign-in link.',
            })}
          </p>
        </header>

        {hasReferral ? (
          <p
            className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-center text-sm font-medium text-emerald-900"
            role="status"
          >
            {intl.formatMessage({
              id: 'auth.login.referralBanner',
              defaultMessage: "You've been invited — get 50% off our fee for your first month.",
            })}
          </p>
        ) : null}

        {reason === 'expired' ? (
          <p
            className="mb-4 rounded-md border border-navy-200 bg-navy-50 px-3 py-2 text-sm text-navy-700"
            role="status"
          >
            {intl.formatMessage({
              id: 'auth.login.expiredBanner',
              defaultMessage: 'Your session expired. Please sign in again.',
            })}
          </p>
        ) : null}

        {sent ? (
          <div className="text-sm text-navy-700">
            <h2 className="mb-2 text-base font-medium text-navy-900">
              {intl.formatMessage({
                id: 'auth.login.sent.title',
                defaultMessage: 'Check your email',
              })}
            </h2>
            <p>
              {intl.formatMessage(
                {
                  id: 'auth.login.sent.body',
                  defaultMessage:
                    'We sent a sign-in link to {email}. The link expires in 15 minutes.',
                },
                { email: email.trim() },
              )}
            </p>
            <button
              type="button"
              onClick={() => {
                setSent(false);
                setError(null);
              }}
              className="mt-4 text-sm text-navy-700 underline hover:text-navy-900"
            >
              {intl.formatMessage({
                id: 'auth.login.sent.tryAgain',
                defaultMessage: 'Use a different email',
              })}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="email">
                  {intl.formatMessage({ id: 'auth.login.email', defaultMessage: 'Email' })}
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  placeholder={intl.formatMessage({
                    id: 'auth.login.emailPlaceholder',
                    defaultMessage: 'you@company.com',
                  })}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              {error ? (
                <p className="text-sm text-[var(--color-danger)]" role="alert">
                  {error}
                </p>
              ) : null}

              <Button
                type="submit"
                className="w-full"
                disabled={pending !== null || email.trim().length === 0}
              >
                {pending === 'email'
                  ? intl.formatMessage({
                      id: 'auth.login.submitting',
                      defaultMessage: 'Sending link…',
                    })
                  : intl.formatMessage({
                      id: 'auth.login.submit',
                      defaultMessage: 'Email me a sign-in link',
                    })}
              </Button>
            </form>

            <div className="flex items-center gap-3 text-xs text-navy-400">
              <span className="h-px flex-1 bg-navy-200" />
              {intl.formatMessage({ id: 'auth.login.or', defaultMessage: 'or' })}
              <span className="h-px flex-1 bg-navy-200" />
            </div>

            {showGoogle ? (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={pending !== null}
                onClick={() => void onGoogle()}
              >
                {pending === 'google'
                  ? intl.formatMessage({
                      id: 'auth.login.googleLoading',
                      defaultMessage: 'Connecting to Google…',
                    })
                  : intl.formatMessage({
                      id: 'auth.login.google',
                      defaultMessage: 'Continue with Google',
                    })}
              </Button>
            ) : null}

            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={pending !== null}
              onClick={() => void onPasskey()}
            >
              {pending === 'passkey'
                ? intl.formatMessage({
                    id: 'auth.login.passkeyLoading',
                    defaultMessage: 'Waiting for your passkey…',
                  })
                : intl.formatMessage({
                    id: 'auth.login.passkey',
                    defaultMessage: 'Sign in with a passkey',
                  })}
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}
